// Two-factor switch and confirmation dialog for the company setting page.
// The host component provides actionUpdateUserCompanySetting, normalizeDataSetup,
// dataSetup, the Vuelidate rules for dataSetup and suspectedLoginValidationAction,
// and a b-modal ref named modalTwoFactor. The dialog body and the failure alert are
// rendered by the host template.
const FAILED_TITLE = {
  on: "Couldn't turn on Two-Factor Authentication.",
  off: "Couldn't turn off Two-Factor Authentication.",
};
const FAILED_BODY = {
  on: 'Nothing was changed and no one was logged out. Please try again.',
  off: 'Nothing was changed. Please try again.',
};
const FIELDS_INVALID = 'Nothing was changed. Fix the highlighted Login and Security fields, then try again.';
const DEACTIVATED = 'Two-Factor Authentication is off. Users now sign in with their password only.';
// sessionStorage flag for the sign-in banner. It survives localStorage.clear() on /logout
// and any 401 redirect that the ended session triggers while the request is still open.
// A browser that blocks storage throws; the banner is optional, so that only hides it.
const TWO_FACTOR_ACTIVATED = 'two_factor_activated';
export const activationBanner = {
  shown() {
    try {
      return sessionStorage.getItem(TWO_FACTOR_ACTIVATED) === '1';
    } catch (e) {
      return false;
    }
  },
  set(on) {
    try {
      if (on) sessionStorage.setItem(TWO_FACTOR_ACTIVATED, '1');
      else sessionStorage.removeItem(TWO_FACTOR_ACTIVATED);
    } catch (e) {
      // Storage is blocked: there is no banner to show or hide.
    }
  },
};

export default {
  data() {
    return {
      twoFactorAuth: false,
      twoFactorSwitch: false,
      twoFactorSaving: false,
      // { title, body } of the failure alert, null while hidden.
      twoFactorError: null,
    };
  },

  computed: {
    twoFactorDialog() {
      if (this.twoFactorSwitch) {
        return { title: 'Activate Two-Factor Authentication?', confirm: 'Activate & log out all users' };
      }
      return { title: 'Deactivate Two-Factor Authentication?', confirm: 'Deactivate' };
    },
  },

  methods: {
    // Only the GET sets the switch: a save response echoes the request, not the stored value.
    setTwoFactorFromSetting(data) {
      this.twoFactorAuth = !!data.two_factor_auth;
      this.twoFactorSwitch = this.twoFactorAuth;
    },

    openTwoFactorDialog(value) {
      this.twoFactorError = null;
      this.twoFactorSwitch = value;
      this.$refs.modalTwoFactor.show();
    },

    restoreTwoFactorSwitch() {
      this.twoFactorSwitch = this.twoFactorAuth;
    },

    cancelTwoFactor() {
      this.$refs.modalTwoFactor.hide();
      this.restoreTwoFactorSwitch();
    },

    // The save sends every Login and Security field, so run the same checks as updateDataSettings:
    // touching them also highlights the invalid ones.
    twoFactorFieldsInvalid() {
      this.$v.dataSetup.$touch();
      this.$v.suspectedLoginValidationAction.$touch();
      return this.$v.dataSetup.$error || this.$v.suspectedLoginValidationAction.$error;
    },

    // Nothing was saved: put the switch back and show the failure alert.
    failTwoFactor(target, body) {
      this.restoreTwoFactorSwitch();
      this.twoFactorError = target
        ? { title: FAILED_TITLE.on, body: body || FAILED_BODY.on }
        : { title: FAILED_TITLE.off, body: body || FAILED_BODY.off };
    },

    // The whole setting object is sent because nocode-service writes every boolean on save.
    // The store runs silent so only this method reports the outcome.
    async confirmTwoFactor() {
      if (this.twoFactorSaving) return;
      const target = this.twoFactorSwitch;
      if (this.twoFactorFieldsInvalid()) {
        this.$refs.modalTwoFactor.hide();
        this.failTwoFactor(target, FIELDS_INVALID);
        return;
      }
      this.twoFactorSaving = true;
      let error = true;
      let message;
      if (target) activationBanner.set(true);
      try {
        this.normalizeDataSetup();
        const res = await this.actionUpdateUserCompanySetting({
          ...this.dataSetup, two_factor_auth: target, _silent: true,
        });
        error = !res || !!res.error;
        message = res && res.message;
      } catch (e) {
        error = true;
      } finally {
        this.twoFactorSaving = false;
        this.$refs.modalTwoFactor.hide();
      }
      if (error) {
        activationBanner.set(false);
        // The server message names the real cause, e.g. another Login and Security field.
        this.failTwoFactor(target, message);
        return;
      }
      this.twoFactorAuth = target;
      if (target) {
        // The server already ended this session, so no toast or wait: the sign-in page
        // shows the activation banner instead.
        this.$router.push('/logout');
        return;
      }
      this.$store.dispatch('jStoreNotificationScreen/toggleSuccess', DEACTIVATED);
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/jMixTwoFactor.js